import { ElementType, Question } from "@archetypes/form";
import { isInputQuestionType } from "./element.util";

export function isChoiceTypeQuestion(elementType?: ElementType) {
  return (
    elementType === ElementType.SingleChoice ||
    elementType === ElementType.MultiChoice ||
    elementType === ElementType.SingleDropdownSelect ||
    elementType === ElementType.MultipleDropdownSelect ||
    elementType === ElementType.Ranking
  );
}

export function isHyperLinkSupportedQuestion(elementType?: ElementType) {
  return (
    elementType !== ElementType.Link &&
    elementType !== ElementType.Expression &&
    elementType !== ElementType.FileUpload &&
    elementType !== ElementType.ImageUpload &&
    elementType !== ElementType.PageBreak &&
    elementType !== ElementType.Signature &&
    elementType !== ElementType.Repeater
  );
}

export function isOptionTypeQuestion(elementType?: ElementType) {
  return (
    elementType === ElementType.Users ||
    elementType === ElementType.Entities ||
    isChoiceTypeQuestion(elementType)
  );
}
export function isElementTitleEntityOrUser(type: ElementType) {
  return type === ElementType.Users || type === ElementType.Entities;
}

export const shouldRenderPlaceholder = (elementType: ElementType): boolean => {
  return (
    elementType !== ElementType.SingleChoice &&
    elementType !== ElementType.MultiChoice &&
    elementType !== ElementType.Ranking &&
    elementType !== ElementType.Signature &&
    elementType !== ElementType.DataModel &&
    elementType !== ElementType.Video &&
    elementType !== ElementType.Rating &&
    elementType !== ElementType.Expression &&
    elementType !== ElementType.YesNo &&
    elementType !== ElementType.Slider &&
    elementType !== ElementType.FileUpload &&
    elementType !== ElementType.Name &&
    elementType !== ElementType.UniqueId &&
    elementType !== ElementType.PreviewImage &&
    elementType !== ElementType.TermsAndConditons &&
    elementType !== ElementType.Frequency &&
    elementType !== ElementType.Repeater
  );
};

export const shouldRenderRequired = (elementType: ElementType): boolean => {
  return (
    elementType !== ElementType.UniqueId &&
    elementType !== ElementType.Expression &&
    elementType !== ElementType.PreviewImage &&
    elementType !== ElementType.Video
  );
};

export const shouldRenderReadOnly = (elementType: ElementType): boolean => {
  return (
    elementType !== ElementType.UniqueId &&
    elementType !== ElementType.Formula &&
    elementType !== ElementType.Aggregator
  );
};

export const isSearchableField = (elementType: ElementType): boolean => {
  return (
    elementType !== ElementType.ImageUpload &&
    elementType !== ElementType.PreviewImage &&
    elementType !== ElementType.Video &&
    elementType !== ElementType.Signature &&
    elementType !== ElementType.Expression &&
    elementType !== ElementType.FileUpload &&
    elementType !== ElementType.Frequency &&
    elementType !== ElementType.PageBreak &&
    elementType !== ElementType.Ranking &&
    elementType !== ElementType.DataModel &&
    elementType !== ElementType.Repeater &&
    elementType !== ElementType.RichText &&
    elementType !== ElementType.Slider
  );
};

export const isGroupByField = (elementType: ElementType): boolean => {
  return (
    elementType === ElementType.SingleDropdownSelect ||
    elementType === ElementType.SingleChoice ||
    elementType === ElementType.MultipleDropdownSelect ||
    elementType === ElementType.MultiChoice ||
    elementType === ElementType.Entities ||
    elementType === ElementType.Users
  );
};

export const isCompositeField = (elementType: ElementType): boolean => {
  return (
    isSearchableField(elementType) &&
    elementType !== ElementType.RichText &&
    elementType !== ElementType.Currency &&
    elementType !== ElementType.Slider
  );
};

export const shouldRenderHint = (elementType: ElementType): boolean => {
  return elementType !== ElementType.Expression && elementType !== ElementType.UniqueId;
};

export const shouldRenderVisibility = (elementType: ElementType): boolean => {
  return elementType !== ElementType.UniqueId;
};

export const shouldRenderDefaultValue = (elementType: ElementType): boolean => {
  return (
    elementType === ElementType.SingleLineInputBox ||
    elementType === ElementType.MultiLineInputBox ||
    elementType === ElementType.MultiChoice ||
    elementType === ElementType.SingleChoice ||
    elementType === ElementType.SingleDropdownSelect ||
    elementType === ElementType.MultipleDropdownSelect ||
    elementType === ElementType.Rating ||
    elementType === ElementType.RichText ||
    elementType === ElementType.DatePicker ||
    elementType === ElementType.DateRangePicker ||
    elementType === ElementType.YesNo ||
    elementType === ElementType.TimePicker ||
    elementType === ElementType.Entities ||
    elementType === ElementType.Users ||
    elementType === ElementType.Number ||
    elementType === ElementType.Aggregator
  );
};

export const isQuestionEligibleForDataViewColumn = (questions: Question[]) => {
  return questions.filter((question) => {
    return ![
      ElementType.Expression,
      ElementType.Video,
      ElementType.PageBreak,
      ElementType.PreviewImage,
    ].includes(question.type);
  });
};

export const isFormatTypeQuestion = (elementType: ElementType): boolean => {
  return (
    elementType === ElementType.DatePicker ||
    elementType === ElementType.SingleLineInputBox ||
    elementType === ElementType.Email ||
    elementType === ElementType.Name ||
    elementType === ElementType.SingleChoice ||
    elementType === ElementType.MultiChoice ||
    elementType === ElementType.SingleDropdownSelect ||
    elementType === ElementType.MultipleDropdownSelect
  );
};

export const isPhoneCurrencyEntityOrUserTypePresent = (questions: Question[]) => {
  let isPhoneNoEnabled: boolean = false;
  let isCurrencyTypeEnabled: boolean = false;
  let isEntityEnabled: boolean = false;
  let isUserEnabled: boolean = false;
  for (const question of questions) {
    isPhoneNoEnabled = !isPhoneNoEnabled
      ? question.type === ElementType.MobileNumber
      : isPhoneNoEnabled;
    isCurrencyTypeEnabled = !isCurrencyTypeEnabled
      ? question.type === ElementType.Currency
      : isCurrencyTypeEnabled;
    isEntityEnabled = !isEntityEnabled ? question.type === ElementType.Entities : isEntityEnabled;
    isUserEnabled = !isUserEnabled ? question.type === ElementType.Users : isUserEnabled;
  }
  return { isPhoneNoEnabled, isCurrencyTypeEnabled, isEntityEnabled, isUserEnabled };
};

export const getQuestionsForRulesOrDataView = ({
  questions,
  currentFormId,
  parentConfig,
  dataModelOrRepeaterFormIds = new Set<string>(),
  showOnlyChildLabel = false,
}: {
  questions: Question[];
  currentFormId: string;
  parentConfig?: { label?: string; questionId?: string };
  dataModelOrRepeaterFormIds?: Set<string>;
  showOnlyChildLabel?: boolean;
}) => {
  let formattedQuestions: Question[] = [];

  dataModelOrRepeaterFormIds.add(currentFormId);
  for (const question of questions) {
    if (question.formId !== currentFormId || !isInputQuestionType(question.type)) {
      continue;
    }

    const label = showOnlyChildLabel
      ? question.content.label
      : parentConfig?.label
      ? parentConfig.label + " > " + question.content.label
      : question.content.label;
    const id = parentConfig?.questionId ? parentConfig.questionId + "." + question.id : question.id;

    formattedQuestions.push({
      ...question,
      id,
      content: { ...question.content, label },
    });

    const dataModelOrRepeaterFormId =
      question.content?.dataModel?.formId || question.content?.repeater?.formId;

    if (
      dataModelOrRepeaterFormId &&
      !dataModelOrRepeaterFormIds.has(dataModelOrRepeaterFormId) // We don't want to add same questions again if a form has multiple data models of a particular form
    ) {
      const nestedQuestions = getQuestionsForRulesOrDataView({
        questions,
        currentFormId: dataModelOrRepeaterFormId,
        parentConfig: {
          label,
          questionId: id,
        },
        dataModelOrRepeaterFormIds,
        showOnlyChildLabel,
      });
      formattedQuestions = [...formattedQuestions, ...nestedQuestions];
      dataModelOrRepeaterFormIds.delete(dataModelOrRepeaterFormId);
    }
  }
  return formattedQuestions;
};
